import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  AlertCircleIcon,
  CalendarIcon,
  ChevronDownIcon,
  ClockIcon,
  ExternalLinkIcon,
  RefreshCwIcon,
} from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Routes } from '@/utils/routes';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  OpsOrderCarrier,
  OpsOrderCarrierService,
  MerchantOrderStatusEnum,
  MerchantOrderTypeEnum,
  type AvailableCarrierService,
} from '@/graphql/generatedTypes';
import {
  carrierLabel,
  isSendCloudCarrier,
} from '@/components/carrier/carrierLabels';
import { PickingJobCard, type PickingJobLink } from './PickingJobCard';
import type { OrderKind } from './types';
import { getValidNextStatuses } from '../../utils/orderTransitionRules';
import { getStatusTranslation } from '../../utils/orderListFilters';
import { formatDateTimeLocalized, formatShortDate } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import { DatePicker } from '@/views/Events/EventOrder/PublicEventOrderForm/components/DatePicker';

function toDateOnlyString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

interface OrderSidebarProps {
  orderKind: OrderKind;
  status: MerchantOrderStatusEnum;
  onStatusChange: (value: MerchantOrderStatusEnum) => void;
  system: string;
  orderType: string;
  warehouse?: string | null;
  // With onWarehouseChange the warehouse is a picker; without, read-only text.
  // A null warehouseUuid selects "automatic", which sends the order to
  // automaticWarehouseUuid.
  warehouseUuid?: string | null;
  automaticWarehouseUuid?: string | null;
  warehouses?: Array<{ id: string; name?: string | null }>;
  onWarehouseChange?: (value: string | null) => void;
  warehouseDisabled?: boolean;
  carrier: OpsOrderCarrier | null;
  onCarrierChange: (value: OpsOrderCarrier) => void;
  carrierService?: OpsOrderCarrierService | null;
  availableCarrierServices?: AvailableCarrierService[];
  carrierPickupDate?: string | null;
  language?: string | null;
  onLanguageChange: (value: string | null) => void;
  packageCount?: number;
  statusDisabled?: boolean;
  fieldsDisabled?: boolean;
  // The carrier has its own permission: RETURN_TO_SENDER opens the address for a
  // corrected replacement while the carrier of the returned shipment stays put.
  carrierDisabled?: boolean;
  errorType?: string | null;
  errorMessage?: string | null;
  lastUpdated?: string | null;
  pickingJob?: PickingJobLink | null;
  // Shown as "Retry now" on the ERROR_RETRYABLE alert; omit to hide it.
  onRetryLabel?: () => void;
  retryingLabel?: boolean;
}

function getFilteredCarriers(
  orderKind: OrderKind,
  availableCarrierServices?: AvailableCarrierService[],
): OpsOrderCarrier[] {
  if (!availableCarrierServices?.length) return CARRIER_OPTIONS;

  const targetService =
    orderKind === 'outbound'
      ? OpsOrderCarrierService.HomeDelivery
      : OpsOrderCarrierService.Dropoff;

  const filtered = availableCarrierServices
    .filter((cs) => cs.services.includes(targetService))
    .map((cs) => cs.carrier);

  return filtered.length > 0 ? filtered : CARRIER_OPTIONS;
}

const CARRIER_SERVICE_LABELS: Record<OpsOrderCarrierService, string> = {
  [OpsOrderCarrierService.HomeDelivery]: 'Home Delivery',
  [OpsOrderCarrierService.Dropoff]: 'Dropoff',
  [OpsOrderCarrierService.Pickup]: 'Pickup',
};

const COURIER_PICKUP_ORDER_TYPES: ReadonlySet<string> = new Set([
  MerchantOrderTypeEnum.DpdPickup,
  MerchantOrderTypeEnum.Pickup,
]);

function formatErrorText(
  errorType?: string | null,
  errorMessage?: string | null,
): string {
  return [errorType, errorMessage].filter(Boolean).join(': ');
}

// Colugo's RETRY_SENDCLOUD_LABELS cron escalates ERROR_RETRYABLE → ERROR 24h
// after the retryable revision was written, anchored on the latest revision's
// ModifiedTimestamp — which is exactly what colugo exposes as `last_updated`.
const RETRYABLE_ERROR_WINDOW_MS = 24 * 60 * 60 * 1000;

// The cron is scheduled at :00, :15, :30 and :45 (cron(0/15 * * * ? *)) and
// queues behind other jobs, so an attempt starts shortly after a quarter hour.
// Every time zone offset is a multiple of 15 minutes, so local quarter hours
// are the same instants as UTC ones.
const RETRY_INTERVAL_MS = 15 * 60 * 1000;

function retryableErrorDeadline(lastUpdated?: string | null): number | null {
  if (!lastUpdated) return null;
  const anchor = new Date(lastUpdated).getTime();
  if (Number.isNaN(anchor)) return null;
  return anchor + RETRYABLE_ERROR_WINDOW_MS;
}

function nextRetryRun(now: number): number {
  return (Math.floor(now / RETRY_INTERVAL_MS) + 1) * RETRY_INTERVAL_MS;
}

function formatInstant(ms: number): string {
  return formatDateTimeLocalized(new Date(ms), i18n.language);
}

// Re-renders every minute so the next-attempt time moves on by itself while
// the page stays open.
function useMinuteClock(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [enabled]);
  return now;
}

export function OrderSidebar({
  orderKind,
  status,
  onStatusChange,
  system,
  orderType,
  warehouse,
  warehouseUuid,
  automaticWarehouseUuid,
  warehouses,
  onWarehouseChange,
  warehouseDisabled,
  carrier,
  onCarrierChange,
  carrierService,
  availableCarrierServices,
  carrierPickupDate,
  language,
  onLanguageChange,
  packageCount,
  statusDisabled,
  fieldsDisabled,
  carrierDisabled,
  errorType,
  errorMessage,
  lastUpdated,
  pickingJob,
  onRetryLabel,
  retryingLabel,
}: OrderSidebarProps) {
  const orderTypeText =
    orderType || (orderKind === 'outbound' ? i18n.t('Common:unknown') : '-');
  const carrierServiceText = COURIER_PICKUP_ORDER_TYPES.has(orderType)
    ? 'Courier pickup'
    : carrierService
      ? CARRIER_SERVICE_LABELS[carrierService]
      : '-';
  const filteredCarriers = getFilteredCarriers(
    orderKind,
    availableCarrierServices,
  );

  const allowedStatuses = getValidNextStatuses(system, status);
  const isReadOnly = allowedStatuses.length === 0 || !!statusDisabled;

  // ERROR_RETRYABLE is non-terminal (a transient SendCloud label failure being
  // auto-retried), so it reuses the ERROR window but explains the retry
  // instead of showing the provider message: the stored one is colugo's own
  // generic "temporarily unavailable" text, which says less than the alert.
  const isRetryableError = status === MerchantOrderStatusEnum.ErrorRetryable;
  const errorText = formatErrorText(errorType, errorMessage);
  const showErrorAlert =
    (status === MerchantOrderStatusEnum.Error && !!errorText) ||
    isRetryableError;
  const now = useMinuteClock(isRetryableError);
  const retryDeadline = isRetryableError
    ? retryableErrorDeadline(lastUpdated)
    : null;
  const nextRun = nextRetryRun(now);
  // The cron escalates instead of attempting once the window has run out, so
  // from the last run inside the window on there is no automatic attempt left
  // to announce.
  const retryWindowEnded = retryDeadline != null && nextRun >= retryDeadline;

  return (
    <div className="tw:sticky tw:top-4 tw:w-[284px] tw:shrink-0 tw:self-start tw:flex tw:flex-col tw:gap-3">
      {showErrorAlert && (
        <Alert variant={isRetryableError ? 'default' : 'destructive'}>
          {isRetryableError ? <ClockIcon /> : <AlertCircleIcon />}
          <AlertTitle>
            {!isRetryableError
              ? i18n.t('Order:sidebarErrorMessage')
              : retryWindowEnded
                ? i18n.t('Order:sidebarLabelRetryEndedTitle')
                : i18n.t('Order:sidebarLabelRetryTitle')}
          </AlertTitle>
          <AlertDescription className="tw:text-wrap">
            {isRetryableError ? (
              <>
                <p>{i18n.t('Order:sidebarLabelRetryExplanation')}</p>
                {retryWindowEnded ? (
                  <p>
                    {i18n.t('Order:sidebarLabelRetryWindowEnded', {
                      error_at: formatInstant(retryDeadline),
                    })}
                  </p>
                ) : (
                  <>
                    <p>
                      {i18n.t('Order:sidebarLabelRetryNextAttempt', {
                        next_at: formatInstant(nextRun),
                      })}
                    </p>
                    {retryDeadline != null && (
                      <p>
                        {i18n.t('Order:sidebarLabelRetryDeadline', {
                          error_at: formatInstant(retryDeadline),
                        })}
                      </p>
                    )}
                  </>
                )}
                {onRetryLabel && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="tw:mt-2"
                    onClick={onRetryLabel}
                    disabled={retryingLabel}
                  >
                    <RefreshCwIcon
                      className={retryingLabel ? 'tw:animate-spin' : undefined}
                    />
                    {i18n.t('Order:sidebarLabelRetryAction')}
                  </Button>
                )}
              </>
            ) : (
              <p className="tw:break-all">{errorText}</p>
            )}
          </AlertDescription>
        </Alert>
      )}
      <Card>
        <CardContent className="tw:flex tw:flex-col tw:gap-4">
          <div className="tw:flex tw:flex-col tw:gap-4">
            <EditableStatusField
              value={status}
              onValueChange={onStatusChange}
              allowedStatuses={allowedStatuses}
              disabled={isReadOnly}
            />
            <SidebarSelectField
              label={i18n.t('MerchantOrder:orderType')}
              value={orderTypeText}
              disabled
            />
            {orderKind === 'outbound' &&
              (onWarehouseChange && warehouses ? (
                <EditableWarehouseField
                  value={warehouseUuid}
                  automaticValue={automaticWarehouseUuid}
                  onValueChange={onWarehouseChange}
                  options={warehouses}
                  disabled={warehouseDisabled}
                />
              ) : (
                <SidebarSelectField
                  label={i18n.t('Order:sidebarWarehouse')}
                  value={warehouse || '-'}
                  disabled
                />
              ))}
            {orderKind === 'inbound' && (
              <p className="tw:text-sm tw:text-muted-foreground">
                {i18n.t('Order:sidebarTotalPackagesToReturn')}
                {'  '}
                {packageCount}
              </p>
            )}
          </div>

          <Separator />

          <div className="tw:flex tw:flex-col tw:gap-4">
            <EditableCarrierField
              value={carrier}
              onValueChange={onCarrierChange}
              disabled={carrierDisabled ?? fieldsDisabled}
              options={filteredCarriers}
            />
            <SidebarSelectField
              label={i18n.t('Order:sidebarCarrierService')}
              value={carrierServiceText}
              disabled
            />
            <SidebarDateField
              label={i18n.t('Event:carrierPickupDate')}
              value={
                carrierPickupDate ? formatShortDate(carrierPickupDate) : '-'
              }
            />
          </div>

          <Separator />

          <EditableLanguageField
            value={language}
            onValueChange={onLanguageChange}
            disabled={fieldsDisabled}
          />
        </CardContent>
      </Card>
      <PickingJobCard pickingJob={pickingJob} />
    </div>
  );
}

/** @deprecated Use `OrderSidebar` with `orderKind="outbound"` */
export function OutboundOrderSidebar(
  props: Omit<OrderSidebarProps, 'orderKind'>,
) {
  return <OrderSidebar {...props} orderKind="outbound" />;
}

/** @deprecated Use `OrderSidebar` with `orderKind="inbound"` */
export function InboundOrderSidebar(
  props: Omit<OrderSidebarProps, 'orderKind'>,
) {
  return <OrderSidebar {...props} orderKind="inbound" />;
}

interface EventOrderSidebarProps {
  status: MerchantOrderStatusEnum;
  onStatusChange: (value: MerchantOrderStatusEnum) => void;
  system: string;
  orderType?: MerchantOrderTypeEnum | null;
  // A null warehouseUuid selects "automatic", which sends the order to
  // automaticWarehouseUuid.
  warehouseUuid?: string | null;
  automaticWarehouseUuid?: string | null;
  warehouses: Array<{ id: string; name?: string | null }>;
  onWarehouseChange: (value: string | null) => void;
  carrier: OpsOrderCarrier | null;
  onCarrierChange: (value: OpsOrderCarrier) => void;
  carrierService?: OpsOrderCarrierService | null;
  availableCarrierServices?: AvailableCarrierService[];
  language?: string | null;
  onLanguageChange: (value: string | null) => void;
  deadline?: string | null;
  carrierPickupDate?: string | null;
  onCarrierPickupDateChange?: (value: string | null) => void;
  eventId: string;
  eventName?: string | null;
  eventAccountId?: string | null;
  eventAccountName?: string | null;
  isSalesOrderSent: boolean;
  salesOrderUrl?: string | null;
  eventDates?: { start?: string | null; end?: string | null };
  eventManager?: string | null;
  deliveryDate?: string | null;
  onDeliveryDateChange?: (value: string | null) => void;
  pickingJob?: PickingJobLink | null;
  isReadOnly?: boolean;
}

export function EventOrderSidebar({
  status,
  onStatusChange,
  system,
  orderType,
  warehouseUuid,
  automaticWarehouseUuid,
  warehouses,
  onWarehouseChange,
  carrier,
  onCarrierChange,
  carrierService,
  availableCarrierServices,
  language,
  onLanguageChange,
  deadline,
  carrierPickupDate,
  onCarrierPickupDateChange,
  eventId,
  eventName,
  eventAccountId,
  eventAccountName,
  isSalesOrderSent,
  salesOrderUrl,
  eventDates,
  eventManager,
  deliveryDate,
  onDeliveryDateChange,
  pickingJob,
  isReadOnly: isReadOnlyProp,
}: EventOrderSidebarProps) {
  const statuses = getValidNextStatuses(system, status, orderType);
  const isReadOnly = statuses.length === 0;
  if (!statuses.includes(status)) {
    statuses.push(status);
  }
  const allowedStatuses = statuses;

  const eventDatesText =
    eventDates?.start && eventDates?.end
      ? `${formatShortDate(eventDates.start)} – ${formatShortDate(eventDates.end)}`
      : eventDates?.start
        ? formatShortDate(eventDates.start)
        : '-';

  // Event orders are outbound deliveries. Ops first picks the logistics:
  // pallets go through a logistic partner (the OTHERS carrier, no dropdown to
  // show), parcels through one of the carriers serving the destination
  // country with HOME_DELIVERY, chosen in the carrier dropdown.
  //
  // Only a SendCloud carrier is a parcel carrier: the label-less ones ship
  // without a parcel, a label or address validation, so offering the in-house
  // carrier here would make "parcel logistics" mean an in-house delivery —
  // which is what resolveEventOrderDefaultCarrier refuses on the backend. Where
  // the country has no SendCloud carrier the list comes out empty and the
  // Parcel option is disabled.
  // There are three carrier classes and two logistics values, so the in-house
  // carrier is neither: it reads as unset rather than borrowing "pallet", which
  // the same control writes as OTHERS. That keeps the Logistics select a real
  // change away from it, and the Carrier field below stays rendered so the page
  // names the carrier the order is actually on.
  const parcelCarriers = getFilteredCarriers(
    'outbound',
    availableCarrierServices,
  ).filter(isSendCloudCarrier);
  const logistics: EventLogistics | null = isSendCloudCarrier(carrier)
    ? 'parcel'
    : carrier === OpsOrderCarrier.Manual
      ? null
      : 'pallet';
  const onLogisticsChange = (next: EventLogistics) => {
    if (next === logistics) return;
    if (next === 'pallet') {
      onCarrierChange(OpsOrderCarrier.Others);
    } else if (parcelCarriers[0]) {
      onCarrierChange(parcelCarriers[0]);
    }
  };
  // Only under pallet logistics is there nothing to choose: the Logistic Partner
  // is what that value means. The in-house carrier is offered as the value the
  // order already holds, so it can be read and replaced, never picked fresh.
  const carrierOptions =
    logistics === 'parcel' || carrier == null
      ? parcelCarriers
      : [carrier, ...parcelCarriers];
  const carrierServiceText = carrierService
    ? CARRIER_SERVICE_LABELS[carrierService]
    : '-';

  return (
    <div className="tw:sticky tw:top-4 tw:w-[284px] tw:shrink-0 tw:self-start tw:flex tw:flex-col tw:gap-3">
      <Card>
        <CardContent className="tw:flex tw:flex-col tw:gap-4">
          <div className="tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:flex-col tw:gap-2">
              <EditableStatusField
                value={status}
                onValueChange={onStatusChange}
                allowedStatuses={allowedStatuses}
                disabled={isReadOnly}
              />
              {status === MerchantOrderStatusEnum.Approved &&
                orderType !== MerchantOrderTypeEnum.EventBufferOrder && (
                  <span className="tw:text-sm tw:text-muted-foreground">
                    {i18n.t('Order:sidebarSalesOrderRequired')}
                  </span>
                )}
            </div>
            <SidebarDateField
              label={i18n.t('Order:sidebarDeadline')}
              value={deadline ? formatShortDate(deadline) : '-'}
            />
            {onCarrierPickupDateChange ? (
              <SidebarEditableDateField
                label={i18n.t('Event:carrierPickupDate')}
                value={carrierPickupDate}
                onChange={onCarrierPickupDateChange}
                disabled={isReadOnlyProp}
              />
            ) : (
              <SidebarDateField
                label={i18n.t('Event:carrierPickupDate')}
                value={
                  carrierPickupDate ? formatShortDate(carrierPickupDate) : '-'
                }
              />
            )}
            {onDeliveryDateChange && (
              <SidebarEditableDateField
                label={i18n.t('Order:columnDeliveryDate')}
                value={deliveryDate}
                onChange={onDeliveryDateChange}
                disabled={isReadOnlyProp}
              />
            )}
            <EditableWarehouseField
              value={warehouseUuid}
              automaticValue={automaticWarehouseUuid}
              onValueChange={onWarehouseChange}
              options={warehouses}
              disabled={isReadOnlyProp}
            />
            <EditableLanguageField
              value={language}
              onValueChange={onLanguageChange}
              disabled={isReadOnlyProp}
            />
          </div>

          <Separator />

          <div className="tw:flex tw:flex-col tw:gap-4">
            <EditableLogisticsField
              value={logistics}
              onValueChange={onLogisticsChange}
              disabled={isReadOnlyProp}
              parcelAvailable={parcelCarriers.length > 0}
            />
            {logistics !== 'pallet' && (
              <EditableCarrierField
                value={carrier}
                onValueChange={onCarrierChange}
                disabled={isReadOnlyProp}
                options={carrierOptions}
              />
            )}
            <SidebarSelectField
              label={i18n.t('Order:sidebarCarrierService')}
              value={carrierServiceText}
              disabled
            />
          </div>

          <Separator />

          <div className="tw:flex tw:flex-col tw:gap-4">
            <SidebarLinkField
              label={i18n.t('Order:sidebarEvent')}
              value={eventName ?? '-'}
              href={Routes.eventDetail(eventId)}
            />
            <SidebarLinkField
              label={i18n.t('Event:eventAccount')}
              value={eventAccountName ?? '-'}
              href={
                eventAccountId
                  ? Routes.eventAccountDetail(eventAccountId)
                  : undefined
              }
            />
            <div className="tw:flex tw:flex-col tw:gap-3">
              <p className="tw:text-sm tw:font-medium">
                {i18n.t('Order:sidebarSalesOrder')}
              </p>
              {salesOrderUrl && (
                <Button
                  variant="outline"
                  className="tw:w-full tw:justify-start tw:font-normal"
                  asChild
                >
                  <a
                    href={salesOrderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLinkIcon className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
                    <span className="tw:truncate">
                      {i18n.t('Order:sidebarSalesOrderLink')}
                    </span>
                  </a>
                </Button>
              )}
              <Button
                variant="outline"
                className="tw:w-full tw:justify-start tw:font-normal"
                disabled={!isSalesOrderSent}
              >
                <span className="tw:truncate">
                  {isSalesOrderSent
                    ? i18n.t('Order:sidebarSalesOrderSent')
                    : i18n.t('Order:sidebarSalesOrderNotSent')}
                </span>
              </Button>
            </div>
            <SidebarDateField
              label={i18n.t('Order:sidebarEventDates')}
              value={eventDatesText}
            />
            <SidebarSelectField
              label={i18n.t('Order:sidebarEventManager')}
              value={eventManager ?? '-'}
              disabled
            />
          </div>
        </CardContent>
      </Card>
      <PickingJobCard pickingJob={pickingJob} />
    </div>
  );
}

interface SidebarLinkFieldProps {
  label: string;
  value: string;
  href?: string;
}

function SidebarLinkField({ label, value, href }: SidebarLinkFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">{label}</p>
      <Button
        variant="outline"
        className="tw:w-full tw:justify-start tw:font-normal"
        asChild={!!href}
        disabled={!href}
      >
        {href ? (
          <a href={href}>
            <ExternalLinkIcon className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
            <span className="tw:truncate">{value}</span>
          </a>
        ) : (
          <span className="tw:truncate">{value}</span>
        )}
      </Button>
    </div>
  );
}

interface SidebarEditableDateFieldProps {
  label: string;
  value?: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

function SidebarEditableDateField({
  label,
  value,
  onChange,
  disabled,
}: SidebarEditableDateFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">{label}</p>
      <DatePicker
        value={value ? new Date(value) : undefined}
        onChange={(date) => onChange(date ? toDateOnlyString(date) : null)}
        buttonProps={{
          className: 'tw:w-full tw:justify-between tw:font-normal',
          disabled,
        }}
      />
    </div>
  );
}

interface SidebarDateFieldProps {
  label: string;
  value: string;
}

export function SidebarDateField({ label, value }: SidebarDateFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">{label}</p>
      <div className="tw:border-input tw:dark:bg-input/30 tw:h-9 tw:w-full tw:rounded-md tw:border tw:px-3 tw:py-2 tw:shadow-xs tw:flex tw:items-center tw:gap-2 tw:opacity-50">
        <p className="tw:flex-1 tw:truncate tw:text-sm tw:text-foreground">
          {value}
        </p>
        <CalendarIcon className="tw:text-muted-foreground tw:size-4 tw:shrink-0" />
      </div>
    </div>
  );
}

const STATUS_OPTIONS = Object.values(MerchantOrderStatusEnum).map((value) => ({
  value,
  getLabel: () => getStatusTranslation(value),
}));

// Fallback list for when the backend sends no availability. OTHERS is included
// because an urgent off-platform shipment must stay selectable even then; MANUAL
// stays out because it is country-gated and only meaningful where the backend
// says so.
const CARRIER_OPTIONS: OpsOrderCarrier[] = [
  OpsOrderCarrier.Dpd,
  OpsOrderCarrier.Dhl,
  OpsOrderCarrier.Ups,
  OpsOrderCarrier.Others,
];

const LANGUAGE_OPTIONS: Array<{ value: string; getLabel: () => string }> = [
  { value: 'en', getLabel: () => i18n.t('Common:langEn') },
  { value: 'de', getLabel: () => i18n.t('Common:langDe') },
  { value: 'fr', getLabel: () => i18n.t('Common:langFr') },
  { value: 'nl', getLabel: () => i18n.t('Common:langNl') },
  { value: 'es', getLabel: () => i18n.t('Common:langEs') },
];

interface EditableStatusFieldProps {
  value: MerchantOrderStatusEnum;
  onValueChange: (value: MerchantOrderStatusEnum) => void;
  allowedStatuses: MerchantOrderStatusEnum[];
  disabled?: boolean;
}

function EditableStatusField({
  value,
  onValueChange,
  allowedStatuses,
  disabled,
}: EditableStatusFieldProps) {
  const options = STATUS_OPTIONS.filter(
    (opt) => opt.value === value || allowedStatuses.includes(opt.value),
  );

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">
        {i18n.t('Order:sidebarStatus')}
      </p>
      <Select
        value={value}
        onValueChange={(next) => onValueChange(next as MerchantOrderStatusEnum)}
        disabled={disabled || options.length <= 1}
      >
        <SelectTrigger className="tw:w-full tw:gap-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="start">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.getLabel()}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type EventLogistics = 'pallet' | 'parcel';

interface EditableLogisticsFieldProps {
  // Null for a carrier that is neither: the in-house one is no more parcel
  // logistics than it is pallet logistics.
  value: EventLogistics | null;
  onValueChange: (value: EventLogistics) => void;
  disabled?: boolean;
  parcelAvailable: boolean;
}

function EditableLogisticsField({
  value,
  onValueChange,
  disabled,
  parcelAvailable,
}: EditableLogisticsFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">
        {i18n.t('Order:sidebarLogistics')}
      </p>
      <Select
        value={value ?? undefined}
        onValueChange={(next) => onValueChange(next as EventLogistics)}
        disabled={disabled}
      >
        <SelectTrigger className="tw:w-full tw:gap-2">
          <SelectValue placeholder="-" />
        </SelectTrigger>
        <SelectContent align="start">
          <SelectItem value="pallet">
            {i18n.t('Order:logisticsPallet')}
          </SelectItem>
          <SelectItem value="parcel" disabled={!parcelAvailable}>
            {i18n.t('Order:logisticsParcel')}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

interface EditableCarrierFieldProps {
  value: OpsOrderCarrier | null;
  onValueChange: (value: OpsOrderCarrier) => void;
  disabled?: boolean;
  options: OpsOrderCarrier[];
}

function EditableCarrierField({
  value,
  onValueChange,
  disabled,
  options,
}: EditableCarrierFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">
        {i18n.t('Order:sidebarCarrier')}
      </p>
      <Select
        value={value ?? undefined}
        onValueChange={(next) => onValueChange(next as OpsOrderCarrier)}
        disabled={disabled}
      >
        <SelectTrigger className="tw:w-full tw:gap-2">
          <SelectValue placeholder="-" />
        </SelectTrigger>
        <SelectContent align="start">
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {carrierLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface EditableLanguageFieldProps {
  value?: string | null;
  onValueChange: (value: string | null) => void;
  disabled?: boolean;
}

function EditableLanguageField({
  value,
  onValueChange,
  disabled,
}: EditableLanguageFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">
        {i18n.t('Order:sidebarLanguage')}
      </p>
      <Select
        value={value ?? ''}
        onValueChange={(next) => onValueChange(next || null)}
        disabled={disabled}
      >
        <SelectTrigger className="tw:w-full tw:gap-2">
          <SelectValue placeholder="-" />
        </SelectTrigger>
        <SelectContent align="start">
          {LANGUAGE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.getLabel()}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface EditableWarehouseFieldProps {
  value?: string | null;
  // Offers an "automatic" choice (a null value) naming this warehouse; omit
  // it to offer explicit warehouses only.
  automaticValue?: string | null;
  onValueChange: (value: string | null) => void;
  options: Array<{ id: string; name?: string | null }>;
  disabled?: boolean;
}

const AUTOMATIC_WAREHOUSE = '__automatic__';

function EditableWarehouseField({
  value,
  automaticValue,
  onValueChange,
  options,
  disabled,
}: EditableWarehouseFieldProps) {
  const offersAutomatic = automaticValue !== undefined;
  const automaticName =
    options.find((option) => option.id === automaticValue)?.name ??
    automaticValue ??
    '-';
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">
        {i18n.t('Order:sidebarWarehouse')}
      </p>
      <Select
        value={value ?? (offersAutomatic ? AUTOMATIC_WAREHOUSE : '')}
        onValueChange={(next) =>
          onValueChange(next && next !== AUTOMATIC_WAREHOUSE ? next : null)
        }
        disabled={disabled}
      >
        <SelectTrigger className="tw:w-full tw:gap-2">
          <SelectValue placeholder="-" />
        </SelectTrigger>
        <SelectContent align="start">
          {offersAutomatic && (
            <SelectItem value={AUTOMATIC_WAREHOUSE}>
              {i18n.t('Order:sidebarWarehouseAutomatic', {
                warehouse: automaticName,
              })}
            </SelectItem>
          )}
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.name ?? option.id}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {offersAutomatic && value != null && value !== automaticValue && (
        <p className="tw:text-muted-foreground tw:text-xs">
          {i18n.t('Order:sidebarWarehouseAutomaticHint', {
            warehouse: automaticName,
          })}
        </p>
      )}
    </div>
  );
}

interface SidebarSelectFieldProps {
  label: string;
  value: string;
  muted?: boolean;
  disabled?: boolean;
}

export function SidebarSelectField({
  label,
  value,
  muted,
  disabled,
}: SidebarSelectFieldProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:font-medium">{label}</p>
      <div
        className={`tw:border-input tw:dark:bg-input/30 tw:h-9 tw:w-full tw:rounded-md tw:border tw:px-3 tw:py-2 tw:shadow-xs tw:flex tw:items-center tw:gap-2 ${
          disabled ? 'tw:opacity-50' : ''
        }`}
      >
        <p
          className={`tw:flex-1 tw:truncate tw:text-sm ${
            muted ? 'tw:text-muted-foreground' : 'tw:text-foreground'
          }`}
        >
          {value}
        </p>
        {!disabled && (
          <ChevronDownIcon className="tw:text-muted-foreground tw:size-4 tw:shrink-0" />
        )}
      </div>
    </div>
  );
}
